import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Copy, Trash2, KeyRound, Check, RefreshCw, Clock, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton, Spinner } from '@/components/ui/misc';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { makeClient } from '@/lib/api';
import { track } from '@/lib/analytics';
import { useT } from '@/lib/i18n';

interface Key {
  id: string;
  name: string;
  role: string;
  last_used_at?: string;
  created_at?: string;
  /** Preenchido numa rotação: instante em que ESTA key para de autenticar. */
  expires_at?: string | null;
  /** Id da key que substituiu esta (rastro da rotação). */
  rotated_to?: string;
}

/** Carências oferecidas na rotação (segundos). 24h é o padrão da API. */
const GRACE = { day: 86_400, week: 604_800, now: 0 } as const;
type GraceChoice = keyof typeof GRACE;

function when(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function Keys() {
  const t = useT();
  const qc = useQueryClient();
  const { data: list, isLoading } = useQuery({
    queryKey: ['keys'],
    queryFn: async (): Promise<Key[]> => ((await makeClient().GET('/keys')).data as { data?: Key[] } | undefined)?.data ?? [],
  });

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState('admin');
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  /** Linha extra da revelação: quando a key ANTIGA para de funcionar (só na rotação). */
  const [revealedNote, setRevealedNote] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState<Key | null>(null);
  const [grace, setGrace] = useState<GraceChoice>('day');

  const refresh = () => qc.invalidateQueries({ queryKey: ['keys'] });

  const create = async () => {
    setBusy(true);
    try {
      const { data, error } = await makeClient().POST('/keys', { body: { name, role: role as 'admin' | 'agent' } });
      if (error) {
        // A recusa da API explica o que fazer (ex.: conta gerida por um parceiro do
        // bZapper Connect, que só emite chave depois de convertida para uso geral) —
        // o aviso genérico escondia isso e parecia defeito.
        toast.error((error as { message?: string } | undefined)?.message || t('keys.generateFailed'));
        return;
      }
      track('api_key_created', { role });
      setRevealedNote(null);
      setRevealed((data as { api_key?: string } | undefined)?.api_key ?? null);
      setCreating(false); setName(''); refresh();
    } finally { setBusy(false); }
  };

  /**
   * Rotaciona: cunha a key nova (mesmo nome, papel e projeto) e deixa a antiga
   * viva pela carência escolhida. Quem ainda usa a antiga continua funcionando
   * até o instante mostrado — é o que permite trocar a credencial sem derrubar
   * a integração no meio do deploy.
   */
  const rotate = async () => {
    if (!rotating) return;
    setBusy(true);
    try {
      const seconds = GRACE[grace];
      const { data, error, response } = await makeClient().POST('/keys/{id}/rotate', {
        params: { path: { id: rotating.id } },
        body: { revoke_in_seconds: seconds },
      });
      if (error) {
        const code = (error as { code?: string } | undefined)?.code;
        toast.error(
          response?.status === 409 && code === 'key_already_revoked' ? t('keys.alreadyRevoked')
            : response?.status === 409 && code === 'key_already_expired' ? t('keys.alreadyExpired')
            : (error as { message?: string } | undefined)?.message || t('keys.rotateFailed'),
        );
        return;
      }
      const d = data as { api_key?: string; old_key_expires_at?: string | null } | undefined;
      track('api_key_rotated', { grace: seconds });
      setRevealedNote(
        d?.old_key_expires_at
          ? t('keys.rotatedOldStops', { when: when(d.old_key_expires_at) })
          : t('keys.rotatedOldDead'),
      );
      setRevealed(d?.api_key ?? null);
      setRotating(null);
      toast.success(t('keys.rotated'));
      refresh();
    } finally { setBusy(false); }
  };

  const revoke = async (id: string) => {
    const { error } = await makeClient().DELETE('/keys/{id}', { params: { path: { id } } });
    if (error) toast.error(t('keys.revokeFailed')); else { toast.success(t('keys.revoked')); refresh(); }
  };

  const copy = () => {
    if (!revealed) return;
    navigator.clipboard.writeText(revealed);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const openRotate = (k: Key) => { setGrace('day'); setRotating(k); };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">{t('keys.subtitle')} <code className="rounded bg-muted px-1">Authorization: Bearer bz_live_…</code></p>
        <Button className="w-full shrink-0 sm:w-auto" onClick={() => setCreating(true)}><Plus className="size-4" /> {t('keys.generate')}</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-5">{[0, 1].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : (list ?? []).length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <KeyRound className="size-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t('keys.empty')}</p>
            </div>
          ) : (
            <>
              <div className="hidden border-b px-5 py-3 text-xs font-medium uppercase tracking-wide text-muted-foreground sm:grid sm:grid-cols-[1fr_auto_auto] sm:items-center sm:gap-4">
                <span>{t('keys.name')}</span>
                <span>{t('keys.role')}</span>
                <span className="text-right">{t('numbers.actions')}</span>
              </div>
              <ul className="divide-y">
                {(list ?? []).map((k) => {
                  // Key rotacionada: ainda autentica, mas tem prazo. O painel diz
                  // o instante exato — sem isso ninguém sabe quando vai quebrar.
                  const expired = !!k.expires_at && new Date(k.expires_at).getTime() <= Date.now();
                  return (
                    <li key={k.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm sm:grid sm:grid-cols-[1fr_auto_auto] sm:items-center sm:gap-4">
                      <div className="min-w-0">
                        <p className="truncate font-medium">{k.name}</p>
                        {k.expires_at && (
                          <p className={'flex items-center gap-1 text-xs ' + (expired ? 'text-destructive' : 'text-[#b5620a]')}>
                            <Clock className="size-3.5 shrink-0" />
                            {expired ? t('keys.expiredAt', { when: when(k.expires_at) }) : t('keys.expiresAt', { when: when(k.expires_at) })}
                          </p>
                        )}
                        {k.rotated_to && !k.expires_at && (
                          <p className="text-xs text-muted-foreground">{t('keys.replaced')}</p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {k.expires_at && (
                          <Badge variant={expired ? 'destructive' : 'warning'}>{expired ? t('keys.expiredBadge') : t('keys.rotatedBadge')}</Badge>
                        )}
                        <Badge variant="secondary">{k.role}</Badge>
                      </div>
                      <div className="flex shrink-0 gap-1 sm:justify-end">
                        {!expired && (
                          <Button size="sm" variant="ghost" onClick={() => openRotate(k)}>
                            <RefreshCw className="size-3.5" /> {t('keys.rotate')}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => revoke(k.id)} className="text-destructive hover:text-destructive"><Trash2 className="size-3.5" /> {t('keys.revoke')}</Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </CardContent>
      </Card>

      {/* gerar */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('keys.dialogTitle')}</DialogTitle>
            <DialogDescription>{t('keys.dialogDesc')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="kname">{t('keys.name')}</Label>
              <Input id="kname" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('keys.namePlaceholder')} />
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t('keys.role')}</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t('keys.roleAdmin')}</SelectItem>
                  <SelectItem value="agent">{t('keys.roleAgent')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>{t('common.cancel')}</Button>
            <Button onClick={create} disabled={busy || !name}>{busy && <Spinner className="border-primary-foreground/40 border-t-primary-foreground" />} {t('keys.generate')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* rotacionar */}
      <Dialog open={!!rotating} onOpenChange={(o) => { if (!o && !busy) setRotating(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RefreshCw className="size-5" /> {t('keys.rotateTitle')}</DialogTitle>
            <DialogDescription>{t('keys.rotateDesc', { name: rotating?.name ?? '' })}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Label>{t('keys.rotateGraceLabel')}</Label>
            <Select value={grace} onValueChange={(v) => setGrace(v as GraceChoice)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="day">{t('keys.graceDay')}</SelectItem>
                <SelectItem value="week">{t('keys.graceWeek')}</SelectItem>
                <SelectItem value="now">{t('keys.graceNow')}</SelectItem>
              </SelectContent>
            </Select>
            <p className={'flex gap-2 rounded-md p-3 text-xs ' + (grace === 'now' ? 'bg-destructive/10 text-destructive' : 'bg-muted/60 text-muted-foreground')}>
              {grace === 'now' ? <TriangleAlert className="mt-px size-4 shrink-0" /> : <Clock className="mt-px size-4 shrink-0" />}
              {grace === 'now' ? t('keys.graceNowWarning') : t('keys.graceWindow')}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRotating(null)} disabled={busy}>{t('common.cancel')}</Button>
            <Button onClick={rotate} disabled={busy}>
              {busy && <Spinner className="border-primary-foreground/40 border-t-primary-foreground" />} {t('keys.rotate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* revelar (uma vez) */}
      <Dialog open={!!revealed} onOpenChange={(o) => !o && setRevealed(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('keys.revealedTitle')}</DialogTitle>
            <DialogDescription>{t('keys.revealedDesc')}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-md border bg-muted p-3">
            <code className="flex-1 truncate text-xs">{revealed}</code>
            <Button size="icon" variant="ghost" onClick={copy} aria-label={t('keys.copyKey')}>{copied ? <Check className="size-4 text-[#3c9a18]" /> : <Copy className="size-4" />}</Button>
          </div>
          {revealedNote && (
            <p className="flex gap-2 rounded-md bg-muted/60 p-3 text-xs text-muted-foreground">
              <Clock className="mt-px size-4 shrink-0" /> {revealedNote}
            </p>
          )}
          <DialogFooter><Button onClick={() => setRevealed(null)}>{t('keys.done')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
